import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Satellite shot tracer — renders one hole's shots on an Esri World Imagery
 * tile layer with a tee marker (T), numbered orange shot markers, a green
 * pin marker (⛳), the dashed orange tracer polyline through shots, and a
 * dashed teal line from the last shot to the pin.
 *
 * Includes a "Replay" button that animates the markers + line drawing in over
 * ~2 seconds — great for sharing.
 *
 * Props:
 *   shots: [{ shot_index, lat, lng, club, distance_yards }]
 *   green: { lat, lng } | null
 *   tee:   { lat, lng } | null  // saved tee OR back-projected estimate from parent
 *   holeNumber: number
 */
export default function ShotMap({ shots, green, tee, holeNumber, dragging = true }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  // Bumping this triggers the useEffect to redraw in animation mode.
  const [replayKey, setReplayKey] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const map = L.map(ref.current, {
      zoomControl: false, attributionControl: false, scrollWheelZoom: false,
      doubleClickZoom: false, dragging,
      tap: dragging,
    });
    mapRef.current = map;
    // Esri satellite + cartocdn labels — matches the course flyover style.
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19, maxNativeZoom: 17, crossOrigin: true,
    }).addTo(map);
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}.png", {
      maxZoom: 19, opacity: 0.7, subdomains: ["a", "b", "c", "d"], crossOrigin: true,
    }).addTo(map);

    const points = [];
    if (tee && tee.lat != null && tee.lng != null) points.push([tee.lat, tee.lng]);
    shots.forEach((s) => points.push([s.lat, s.lng]));
    if (green && green.lat != null && green.lng != null) points.push([green.lat, green.lng]);

    if (points.length === 0) {
      map.setView([-18.1958, 177.3805], 16);
      return;
    }

    const animate = replayKey > 0;
    let delay = animate ? 0 : 0;
    const STAGGER = animate ? 220 : 0;

    // Tee marker — small grey "T" at the start of the line.
    if (tee && tee.lat != null && tee.lng != null) {
      const teeIcon = L.divIcon({
        className: "tee-marker",
        html: `<div style="background:#0F2537;color:white;width:22px;height:22px;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:11px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.45)">T</div>`,
        iconSize: [22, 22], iconAnchor: [11, 11],
      });
      L.marker([tee.lat, tee.lng], { icon: teeIcon, opacity: animate ? 0 : 1 })
        .addTo(map)
        .bindTooltip("Tee box", { direction: "top" });
    }

    // Polyline through tee → shots, drawn in segments so we can animate it.
    const linePoints = [];
    if (tee && tee.lat != null && tee.lng != null) linePoints.push([tee.lat, tee.lng]);

    shots.forEach((s, i) => {
      const numberedIcon = L.divIcon({
        className: "shot-marker",
        html: `<div style="background:#E05D3A;color:white;width:24px;height:24px;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:11px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.35)">${s.shot_index || i + 1}</div>`,
        iconSize: [24, 24], iconAnchor: [12, 12],
      });
      const m = L.marker([s.lat, s.lng], { icon: numberedIcon, opacity: animate ? 0 : 1 }).addTo(map);
      const distFromPrev = s.distance_yards != null
        ? `${s.distance_yards} yds` : "";
      m.bindTooltip(`${s.club}${distFromPrev ? " · " + distFromPrev : ""}`, { direction: "top" });
      if (animate) {
        setTimeout(() => m.setOpacity(1), delay);
        delay += STAGGER;
      }
      linePoints.push([s.lat, s.lng]);
    });

    // Tracer line tee→shots (orange dashed).
    if (linePoints.length >= 2) {
      const line = L.polyline(linePoints, {
        color: "#E05D3A", weight: 3, opacity: 0.92, dashArray: "8 5",
      });
      if (!animate) {
        line.addTo(map);
      } else {
        // Add an empty line then progressively extend it as the markers light up.
        const animLine = L.polyline([linePoints[0]], {
          color: "#E05D3A", weight: 3, opacity: 0.92, dashArray: "8 5",
        }).addTo(map);
        for (let i = 1; i < linePoints.length; i++) {
          // eslint-disable-next-line no-loop-func
          setTimeout(() => animLine.setLatLngs(linePoints.slice(0, i + 1)),
            STAGGER * (i - 1) + 80);
        }
      }
    }

    // Pin marker + dashed teal "approach" segment from last shot to green.
    if (green && green.lat != null && green.lng != null) {
      const flagIcon = L.divIcon({
        className: "green-marker",
        html: `<div style="background:#1E5631;color:white;width:26px;height:26px;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.45)">⛳</div>`,
        iconSize: [26, 26], iconAnchor: [13, 13],
      });
      const flag = L.marker([green.lat, green.lng], { icon: flagIcon, opacity: animate ? 0 : 1 }).addTo(map)
        .bindTooltip("Green centre", { direction: "top" });
      if (animate) setTimeout(() => flag.setOpacity(1), delay + STAGGER);
      const last = shots.length ? [shots[shots.length - 1].lat, shots[shots.length - 1].lng]
                                 : (tee ? [tee.lat, tee.lng] : null);
      if (last) {
        L.polyline([last, [green.lat, green.lng]], {
          color: "#1E5631", weight: 2.5, opacity: 0.85, dashArray: "4 4",
        }).addTo(map);
      }
    }

    map.fitBounds(points, { padding: [28, 28], maxZoom: 18 });
    // Replay-mode courtesy: invalidate size after a tick so labels reflow.
    setTimeout(() => map.invalidateSize(), 60);

    return () => { map.remove(); mapRef.current = null; };
  }, [shots, green, tee, replayKey]);

  const hasShots = shots && shots.length > 0;

  return (
    <div className="relative">
      <div
        ref={ref}
        data-testid={`shot-map-${holeNumber}`}
        className="w-full h-64 md:h-72 rounded-xl overflow-hidden border border-[#E2E0D8] bg-[#0F2537]/5"
      />
      {hasShots && (
        <button
          onClick={() => setReplayKey((k) => k + 1)}
          data-testid={`shot-map-replay-${holeNumber}`}
          className="absolute top-2 right-2 z-[400] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 hover:bg-white text-[#0F2537] text-[11px] font-bold shadow-md transition"
          title="Replay shot tracer"
        >▶ Replay</button>
      )}
    </div>
  );
}
